import { normalizeExperience } from './normalizeExperience';

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Normalize launch URLs so duplicate listings (e.g. For You + category) dedupe correctly. */
export function normalizeExperienceUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0];
      return id ? `youtube:${id}` : raw.toLowerCase();
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = parsed.searchParams.get('v');
      return id ? `youtube:${id}` : raw.toLowerCase();
    }

    const path = parsed.pathname.replace(/\/$/, '').toLowerCase();
    return `${host}${path}${parsed.search}`.toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

/** Stable identity for deduping the same real-world experience across multiple list entries. */
export function getExperienceIdentityKey(exp) {
  const normalized = normalizeExperience(exp);
  if (!normalized?.id) return '';

  const urlKey = normalizeExperienceUrl(normalized.launchUrl || normalized.url);
  if (urlKey) return `url:${urlKey}`;

  if (normalized.slug) return `slug:${String(normalized.slug).toLowerCase()}`;

  return `id:${String(normalized.id)}`;
}

export function isSameExperience(a, b) {
  if (!a || !b) return false;
  if (String(a.id) === String(b.id)) return true;

  const keyA = getExperienceIdentityKey(a);
  const keyB = getExperienceIdentityKey(b);
  if (!keyA || !keyB) return false;

  if (keyA.startsWith('url:') && keyA === keyB) return true;
  if (keyA.startsWith('slug:') && keyA === keyB) return true;

  return false;
}

/**
 * Returns experiences sorted by distance from anchor, excluding the anchor itself.
 */
export function getNearbyExperiences(anchorExp, allExperiences, limit = 6) {
  const anchor = normalizeExperience(anchorExp);
  if (!anchor?.isPhysicalVenue || anchor.venueLat == null || anchor.venueLng == null) {
    return [];
  }

  return allExperiences
    .filter((e) => !isSameExperience(anchor, e))
    .map((e) => {
      const n = normalizeExperience(e);
      if (n.venueLat == null || n.venueLng == null) return null;
      return {
        exp: e,
        distance: haversineKm(anchor.venueLat, anchor.venueLng, n.venueLat, n.venueLng)
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)
    .map((x) => x.exp);
}

export function getExperiencesByCreator(creatorId, allExperiences, excludeExp, limit = 6) {
  if (!creatorId) return [];
  return allExperiences
    .filter((e) => e.creatorId === creatorId && !isSameExperience(excludeExp, e))
    .slice(0, limit);
}

export function getSimilarExperiences(anchorExp, allExperiences, limit = 6) {
  const anchor = normalizeExperience(anchorExp);
  if (!anchor?.primaryFormat || !anchor.id) return [];

  const seenIds = new Set();
  const results = [];

  for (const e of allExperiences) {
    if (!e?.id || seenIds.has(String(e.id))) continue;
    if (isSameExperience(anchor, e)) continue;

    const candidate = normalizeExperience(e);
    if (candidate.primaryFormat !== anchor.primaryFormat) continue;

    seenIds.add(String(e.id));
    results.push(e);
    if (results.length >= limit) break;
  }

  return results;
}

export function getCreatorOtherExperiences(creatorId, allExperiences, excludeExp, limit = 3) {
  return getExperiencesByCreator(creatorId, allExperiences, excludeExp, limit);
}
